import * as Yup from "yup";
import { WorkOrderItemType } from "features/WorkOrders/constants/work-order-item.constants";
import { codeSchema } from "utils/schemas";

const BOQ_TYPES = [
  WorkOrderItemType.MATERIAL,
  WorkOrderItemType.LABOR,
  WorkOrderItemType.SERVICE,
] as const;

function hasProductId(productId: unknown): boolean {
  if (productId === "" || productId == null) return false;
  const n = Number(productId);
  return Number.isFinite(n) && n > 0;
}

function isPlaceholderBoqRow(row: unknown): boolean {
  if (!row || typeof row !== "object") return true;
  const r = row as Record<string, unknown>;
  const desc = String(r.description ?? "").trim();
  const unit = String(r.unit ?? "").trim();
  const type = String(r.type ?? "");
  const qty = Number(r.quantity);
  const rate = Number(r.rate);
  const pid = r.productId;

  return (
    !hasProductId(pid) &&
    desc === "" &&
    unit === "" &&
    type === WorkOrderItemType.SERVICE &&
    (Number.isFinite(qty) ? qty === 1 : true) &&
    (Number.isFinite(rate) ? rate === 0 : true)
  );
}

const itemSchema = Yup.object({
  productId: Yup.mixed().nullable().optional(),
  lineId: Yup.number().nullable().optional(),
  onHandQty: Yup.mixed().nullable().optional(),
  description: Yup.string().nullable().optional(),
  quantity: Yup.number()
    .required("Quantity is required")
    .moreThan(0, "Quantity must be greater than 0"),
  unit: Yup.string().nullable().optional(),
  rate: Yup.mixed().nullable().optional(),
  type: Yup.string()
    .oneOf([...BOQ_TYPES], "Invalid line type")
    .required("Type is required"),
  sourceType: Yup.string().nullable().optional(),
  quotationItemId: Yup.mixed().nullable().optional(),
  originalQuantity: Yup.mixed().nullable().optional(),
}).test("boq-line-rules", function (val) {
  if (!val) return true;
  if (isPlaceholderBoqRow(val)) return true;

  const withProduct = hasProductId(val.productId);
  if (!withProduct) {
    const desc = String(val.description ?? "").trim();
    if (!desc) {
      return this.createError({
        path: `${this.path}.description`,
        message: "Description is required when no product is selected",
      });
    }
    const unit = String(val.unit ?? "").trim();
    if (!unit) {
      return this.createError({
        path: `${this.path}.unit`,
        message: "Unit is required when no product is selected",
      });
    }
    const rateRaw = val.rate;
    if (
      rateRaw == null ||
      rateRaw === "" ||
      (typeof rateRaw === "string" && !String(rateRaw).trim())
    ) {
      return this.createError({
        path: `${this.path}.rate`,
        message: "Rate is required when no product is selected",
      });
    }
    if (Number(rateRaw) < 0) {
      return this.createError({
        path: `${this.path}.rate`,
        message: "Rate must be ≥ 0",
      });
    }
  }
  return true;
});

const schema = Yup.object({
  code: codeSchema,

  title: Yup.string().required("The title field is required"),

  clientId: Yup.mixed<number | string>()
    .required("The client field is required")
    .test(
      "client-id",
      "Please select a client",
      (v) =>
        v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) > 0,
    ),

  startDate: Yup.date().required("The start date field is required"),

  endDate: Yup.date()
    .required("The end date field is required")
    .test(
      "is-full-year-anniversary",
      "End date must be a whole number of years (1, 2, 3…) after the start date",
      function (value) {
        const { startDate } = this.parent;

        if (!value || !startDate) return true;

        const start = new Date(startDate);
        const end = new Date(value);

        const yearDiff = end.getFullYear() - start.getFullYear();
        if (yearDiff < 1) return false;

        return (
          end.getMonth() === start.getMonth() &&
          end.getDate() === start.getDate()
        );
      },
    ),

  numberOfVisits: Yup.number()
    .min(1, "At least 1 visit is required")
    .max(12, "Number of visits cannot exceed 12 per year")
    .required("The number of visits field is required"),

  checklistTemplateId: Yup.mixed<number | string>()
    .nullable()
    .optional()
    .test(
      "checklist-template-id",
      "Select a valid checklist template or leave empty",
      (v) =>
        v === "" || v == null || (Number.isFinite(Number(v)) && Number(v) > 0),
    ),

  items: Yup.array().of(itemSchema).optional(),

  agreedAmount: Yup.number()
    .typeError("Must be a number")
    .min(0, "Cannot be negative")
    .nullable()
    .optional(),
});

export default schema;
